import { useEffect, useState } from 'react';
import api from '../api/axios';

interface Location {
  id: number;
  name: string;
  address: string;
  is_catalog: number;
  is_po: number;
}

const emptyForm = { name: '', address: '', is_catalog: false, is_po: false };

export default function Lokasi() {
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState<Location | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [error, setError] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/locations/getAllLocations');
      setLocations(res.data);
    } catch {
      setError('Gagal memuat data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const openAdd = () => {
    setEditItem(null);
    setForm(emptyForm);
    setError('');
    setShowModal(true);
  };

  const openEdit = (loc: Location) => {
    setEditItem(loc);
    setForm({ name: loc.name, address: loc.address, is_catalog: !!loc.is_catalog, is_po: !!loc.is_po });
    setError('');
    setShowModal(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (editItem) {
        await api.put(`/locations/updateLocations/${editItem.id}`, form);
      } else {
        await api.post('/locations/createLocations', form);
      }
      setShowModal(false);
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Gagal menyimpan');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/locations/deleteLocations/${deleteId}`);
      setDeleteId(null);
      fetchData();
    } catch {
      setError('Gagal menghapus');
    }
  };

  const handleToggleCatalog = async (id: number) => {
    setTogglingId(id);
    try {
      const res = await api.patch(`/locations/toggleCatalog/${id}`);
      setLocations((prev) => prev.map((l) => l.id === id ? { ...l, is_catalog: res.data.is_catalog } : l));
    } catch {
      setError('Gagal mengubah status katalog');
    } finally {
      setTogglingId(null);
    }
  };

  const handleTogglePO = async (id: number) => {
    setTogglingId(id);
    try {
      const res = await api.patch(`/locations/togglePO/${id}`);
      setLocations((prev) => prev.map((l) => l.id === id ? { ...l, is_po: res.data.is_po } : l));
    } catch {
      setError('Gagal mengubah status PO');
    } finally {
      setTogglingId(null);
    }
  };

  const catalogCount = locations.filter((l) => l.is_catalog).length;
  const poCount = locations.filter((l) => l.is_po).length;

  const Toggle = ({ active, onToggle, disabled }: { active: boolean; onToggle: () => void; disabled?: boolean }) => (
    <button type="button" onClick={onToggle} disabled={disabled}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${active ? 'bg-emerald-500' : 'bg-gray-200'} ${disabled ? 'opacity-60 cursor-wait' : ''}`}>
      <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ${active ? 'translate-x-5' : 'translate-x-0'}`} />
    </button>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Lokasi</h1>
          <p className="text-gray-500 text-sm mt-1">
            {locations.length} lokasi &bull;{' '}
            <span className="text-emerald-600 font-medium">{catalogCount} di katalog</span>
            {poCount > 0 && <span className="text-orange-500 font-medium"> &bull; {poCount} lokasi PO</span>}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {catalogCount > 0 && (
            <a href="/katalog" target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 border border-emerald-500 text-emerald-600 px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-emerald-50 transition">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              Lihat Katalog
            </a>
          )}
          <button onClick={openAdd}
            className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-indigo-700 transition">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
            Tambah Lokasi
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">{error}</div>}

      <div className="bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-3 flex items-start gap-3">
        <svg className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-sm text-indigo-700">
          <strong>Tampil di Katalog</strong> — produk terlihat pelanggan. &nbsp;
          <strong>Lokasi PO</strong> — produk ditandai sebagai Pre-Order (pesan dulu, stok menyusul).
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-3 flex items-center justify-center h-48 text-gray-400">
            <svg className="animate-spin w-6 h-6 mr-2" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Memuat...
          </div>
        ) : locations.length === 0 ? (
          <div className="col-span-3 bg-white rounded-2xl border border-gray-100 p-12 text-center text-gray-400">
            Belum ada lokasi
          </div>
        ) : locations.map((loc) => (
          <div key={loc.id}
            className={`bg-white rounded-2xl shadow-sm border transition ${loc.is_po ? 'border-orange-200 ring-1 ring-orange-100' : loc.is_catalog ? 'border-emerald-200 ring-1 ring-emerald-100' : 'border-gray-100'}`}>
            <div className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${loc.is_po ? 'bg-orange-50 text-orange-500' : loc.is_catalog ? 'bg-emerald-50 text-emerald-600' : 'bg-indigo-50 text-indigo-600'}`}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div className="flex items-center gap-1">
                  {loc.is_po && <span className="text-xs bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full font-medium">PO</span>}
                  {loc.is_catalog && <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">Katalog</span>}
                  <button onClick={() => openEdit(loc)} className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg transition">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                  </button>
                  <button onClick={() => setDeleteId(loc.id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                  </button>
                </div>
              </div>
              <h3 className="font-semibold text-gray-800">{loc.name}</h3>
              <p className="text-sm text-gray-500 mt-1">{loc.address || '-'}</p>
            </div>

            <div className="border-t border-gray-50 divide-y divide-gray-50">
              {/* Toggle Katalog */}
              <div className="px-5 py-3 flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-gray-700">Tampil di Katalog</p>
                  <p className="text-xs text-gray-400 mt-0.5">{loc.is_catalog ? 'Aktif' : 'Tidak aktif'}</p>
                </div>
                <Toggle active={!!loc.is_catalog} onToggle={() => handleToggleCatalog(loc.id)} disabled={togglingId === loc.id} />
              </div>
              {/* Toggle PO */}
              <div className="px-5 py-3 flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-gray-700">Lokasi Pre-Order (PO)</p>
                  <p className="text-xs text-gray-400 mt-0.5">{loc.is_po ? 'Produk ditandai PO' : 'Ready stock'}</p>
                </div>
                <Toggle active={!!loc.is_po} onToggle={() => handleTogglePO(loc.id)} disabled={togglingId === loc.id} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal Form */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-800">{editItem ? 'Edit Lokasi' : 'Tambah Lokasi'}</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              {error && <p className="text-red-500 text-sm">{error}</p>}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Nama Lokasi *</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="Nama lokasi" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Alamat *</label>
                <textarea required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={3}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none" placeholder="Alamat lengkap" />
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                <div>
                  <p className="text-sm font-medium text-gray-700">Tampil di Katalog</p>
                  <p className="text-xs text-gray-400">Produk lokasi ini bisa dilihat pelanggan</p>
                </div>
                <button type="button" onClick={() => setForm({ ...form, is_catalog: !form.is_catalog })}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ${form.is_catalog ? 'bg-emerald-500' : 'bg-gray-200'}`}>
                  <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ${form.is_catalog ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </div>
              <div className="flex items-center justify-between p-3 bg-orange-50 rounded-xl">
                <div>
                  <p className="text-sm font-medium text-orange-800">Lokasi Pre-Order (PO)</p>
                  <p className="text-xs text-orange-400">Produk ditandai badge PO di katalog</p>
                </div>
                <button type="button" onClick={() => setForm({ ...form, is_po: !form.is_po })}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ${form.is_po ? 'bg-orange-400' : 'bg-gray-200'}`}>
                  <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ${form.is_po ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 border border-gray-200 text-gray-700 py-2.5 rounded-xl text-sm font-medium hover:bg-gray-50 transition">Batal</button>
                <button type="submit" disabled={saving} className="flex-1 bg-indigo-600 text-white py-2.5 rounded-xl text-sm font-medium hover:bg-indigo-700 disabled:opacity-60 transition">
                  {saving ? 'Menyimpan...' : 'Simpan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirm Delete */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Hapus Lokasi?</h3>
            <p className="text-gray-500 text-sm mb-6">Tindakan ini tidak bisa dibatalkan.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 border border-gray-200 text-gray-700 py-2.5 rounded-xl text-sm font-medium hover:bg-gray-50 transition">Batal</button>
              <button onClick={handleDelete} className="flex-1 bg-red-500 text-white py-2.5 rounded-xl text-sm font-medium hover:bg-red-600 transition">Hapus</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
